import { deriveCompanyIndemnitorsMissingAddress } from "@portal/api/account";
import {
	deriveAdminAccountIndemnitySummary,
	useAdminFetchAccountCompanies,
	useAdminFetchAccountIndividuals,
	useAdminFetchAccountLatestIndemnityAgreement,
} from "@portal/api/account/admin";
import { ReportedRetryableLoadError } from "@portal/components/features/load-errors";
import { Card, Spinner, Text } from "@portal/components/primitives";
import type { FC, ReactNode } from "react";
import { AccountTabProps } from "../../../AccountLayout";
import { useAccountTabsNavigation } from "../../../useAccountTabNavigation";
import { AdminAccountIndemnityAgreementDetails } from "./AdminAccountIndemnityAgreementDetails";
import { AdminAccountIndemnityAgreementStatus } from "./AdminAccountIndemnityAgreementStatus";
import { AdminAccountRequiredIndemnitorsList } from "./AdminAccountRequiredIndemnitorsList";
import { AdminAccountWaivedIndemnitorsList } from "./AdminAccountWaivedIndemnitorsList";

type AdminAccountIndemnityTabProps = AccountTabProps;

const IndemnitySectionCard = ({
	title,
	testId,
	children,
}: {
	title: string;
	testId: string;
	children: ReactNode;
}) => (
	<Card elevation="raised" className="rounded-xl" data-testid={testId}>
		<Card.Body className="flex w-full flex-col p-6 space-y-5">
			<Text type="title" color="primary">
				{title}
			</Text>
			{children}
		</Card.Body>
	</Card>
);

export const AdminAccountIndemnityTab: FC<AdminAccountIndemnityTabProps> = ({ accountId }) => {
	const { companies, companiesError, companiesIsFetching, refetchCompanies } =
		useAdminFetchAccountCompanies(accountId);
	const { individuals, individualsError, individualsIsFetching, refetchIndividuals } =
		useAdminFetchAccountIndividuals(accountId);
	const {
		latestIndemnityAgreement,
		latestIndemnityAgreementError,
		latestIndemnityAgreementIsFetching,
		latestIndemnityAgreementIsLoading,
		refetchLatestIndemnityAgreement,
	} = useAdminFetchAccountLatestIndemnityAgreement(accountId);

	const error = companiesError ?? individualsError ?? latestIndemnityAgreementError;
	const indemnitySummary =
		companies && individuals
			? deriveAdminAccountIndemnitySummary(companies, individuals)
			: undefined;

	const navigateTabs = useAccountTabsNavigation();
	const companiesMissingAddress = companies
		? deriveCompanyIndemnitorsMissingAddress(companies)
		: [];
	const blockedOnAddress = latestIndemnityAgreement === null && companiesMissingAddress.length > 0;
	const missingAddress = blockedOnAddress
		? {
				companyIds: new Set<string>(companiesMissingAddress.map((company) => company.id)),
				onAddAddress: () => navigateTabs({ tabId: "details", subsectionId: "companies" }),
			}
		: undefined;

	const showWaived = Boolean(
		(indemnitySummary?.indemnityWaivers?.companies ?? []).length > 0 ||
			(indemnitySummary?.indemnityWaivers?.individuals ?? []).length > 0,
	);

	return (
		<div className="flex flex-col space-y-5 pb-[125px]">
			<div className="flex items-center space-x-[10px]">
				<Text type="heading" color="primary" className="flex-1">
					Indemnity
				</Text>
				{!error && (
					<AdminAccountIndemnityAgreementStatus
						latestIndemnityAgreement={latestIndemnityAgreement}
						isLoading={latestIndemnityAgreementIsLoading}
					/>
				)}
			</div>
			{error ? (
				<ReportedRetryableLoadError
					error={error}
					resource="account indemnity"
					retrying={
						companiesIsFetching || individualsIsFetching || latestIndemnityAgreementIsFetching
					}
					onRetry={() => {
						refetchCompanies();
						refetchIndividuals();
						refetchLatestIndemnityAgreement();
					}}
				/>
			) : !indemnitySummary || latestIndemnityAgreement === undefined ? (
				<Spinner size="lg" />
			) : (
				<>
					<IndemnitySectionCard
						title="Required Indemnitors"
						testId="admin-account-required-indemnitors-card"
					>
						<AdminAccountRequiredIndemnitorsList
							indemnitySummary={indemnitySummary}
							missingAddress={missingAddress}
						/>
					</IndemnitySectionCard>
					{showWaived && (
						<IndemnitySectionCard
							title="Waived Indemnitors"
							testId="admin-account-waived-indemnitors-card"
						>
							<AdminAccountWaivedIndemnitorsList indemnitySummary={indemnitySummary} />
						</IndemnitySectionCard>
					)}
					<IndemnitySectionCard
						title="Indemnity Agreement"
						testId="admin-account-indemnity-agreement-card"
					>
						<AdminAccountIndemnityAgreementDetails
							accountId={accountId}
							indemnitySummary={indemnitySummary}
							latestIndemnityAgreement={latestIndemnityAgreement}
							blockedOnAddress={blockedOnAddress}
						/>
					</IndemnitySectionCard>
				</>
			)}
		</div>
	);
};
